import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useState, useEffect, useRef } from "react";
import useStore from "../../../../../helpers/useStore";
import { DoctorContext } from "../../../../../stores/Doctor/doctor.provider";
import { ClinicForModal } from "../../../../DoctorManagement/DoctorTabContent/doctor-client-types";
import LoadingSpinner from "../../../../BIDashboard/components/LoadingSpinner";
import { observer } from "mobx-react";
import ClinicInfoDisplay from "../../../../DoctorManagement/DoctorClinicDialog/ClinicInfoDisplay";
import ClinicInfoEditForm from "../../../../DoctorManagement/DoctorClinicDialog/ClinicInfoEditForm";
import {
  EditDoctorDialog,
  EditClinicDialog,
  ErrorDialog,
} from "../../../../DoctorManagement/DoctorClinicDialog/DoctorClinicAuxDialoges";
import { useCreateDoctorWithClinic, useUpdateDoctor } from "../../../../../stores/Doctor/doctor.mutations";

/** Rx types that allow creating doctors/clinics without a 9-digit LANR/BSNR */
const FLEXIBLE_ID_RX_TYPES = ["Privat/Selbstzahler", "Berufsgenossenschaft", "Bundeswehr"];

interface VerordnungDialogProps {
  openAddEditDialog: boolean;
  handleCloseAddEditDialog: () => void;
  clinicId: string;
  setCreationResponse: any;
  doctorId: string;
  currentClinic: any;
  /** Full doctor object so we can read LANR for display */
  currentDoctor?: any;
  /** When set, ID validation is relaxed for flexible Rx types */
  rxType?: string;
  /** Pre-fill doctor last name from autocomplete search */
  initialDoctorName?: string;
  /** When true, show selected doctor + other doctors (no "Neuen Arzt anlegen" form) */
  isEditMode?: boolean;
}

const VerordnungDialog: React.FC<VerordnungDialogProps> = observer(({
  openAddEditDialog,
  handleCloseAddEditDialog,
  clinicId,
  setCreationResponse,
  doctorId,
  currentClinic,
  currentDoctor,
  rxType,
  initialDoctorName,
  isEditMode,
}) => {
  const DoctorStore = useStore(DoctorContext);
  const {
    createDoctorWithClinic,
    getClinicById,
    getDoctorById,
    updateClinic,
    searchDoctor,
    searchClinic,
    updateDoctorLANR,
    updateClinicBSNR,
  } = DoctorStore;

  const initialClinicForm = {
    id: "",
    businessName: "",
    phone1: "",
    fax: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    zip: "",
    email: "",
    notes: "",
    methodForDrReport: "",
    methodForRxOrder: "",
  };

  enum ReportMethod {
    Email = "Email",
    Fax = "Fax",
    Post = "Post",
  }

  const [clinicForm, setClinicForm] = useState(initialClinicForm);
  const [existingDoctors, setExistingDoctors] = useState<any[]>([]);
  const [newDoctorForm, setNewDoctorForm] = useState<{
    id: string;
    salutation: string;
    firstNames: string;
    lastName: string;
  } | null>(null);
  const [isClinicValid, setIsClinicValid] = useState(false);
  const [isDoctorValid, setIsDoctorValid] = useState(false);
  // Last BSNR/LANR we already looked up, so re-emitted change events for an
  // unchanged 9-digit value don't repeat the request.
  const lastClinicLookupRef = useRef<string | null>(null);
  const lastDoctorLookupRef = useRef<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  // Store the actual entity UUIDs separately from displayed BSNR/LANR
  const [existingClinicEntityId, setExistingClinicEntityId] = useState("");
  const [existingDoctorEntityId, setExistingDoctorEntityId] = useState("");
  // Edit sub-dialogs
  const [openEditDoctorDialog, setOpenEditDoctorDialog] = useState(false);
  const [doctorToEdit, setDoctorToEdit] = useState<any>(null);
  const [openEditClinicDialog, setOpenEditClinicDialog] = useState(false);
  const [clinicToEdit, setClinicToEdit] = useState<any>(null);

  // Duplicate-detection state
  const [doctorMatches, setDoctorMatches] = useState<any[]>([]);
  const [loadingDoctorMatches, setLoadingDoctorMatches] = useState(false);
  const [clinicNameMatches, setClinicNameMatches] = useState<any[]>([]);
  const [clinicAddressMatches, setClinicAddressMatches] = useState<any[]>([]);
  const [loadingClinicMatches, setLoadingClinicMatches] = useState(false);
  // Tracks whether any edit happened in edit mode so we can propagate to parent on close
  const [editsHappened, setEditsHappened] = useState(false);
  const [pendingAttach, setPendingAttach] = useState<
    | { kind: "doctor"; entity: any; newLANR: string }
    | { kind: "clinic"; entity: any; newBSNR: string }
    | null
  >(null);

  const updateDoctorMutation = useUpdateDoctor();
  const createDoctorWithClinicMutation = useCreateDoctorWithClinic();

  // Initialize form state when dialog opens
  useEffect(() => {
    if (!openAddEditDialog) return;

    if (currentClinic?.id) {
      setExistingClinicEntityId(currentClinic.id);
      setClinicForm({
        id: currentClinic.BSNR || "",
        businessName: currentClinic.contactInfo?.businessName || "",
        phone1: currentClinic.contactInfo?.phone1 || "",
        fax: currentClinic.contactInfo?.fax || "",
        addressLine1: currentClinic.contactInfo?.addressLine1 || "",
        addressLine2: currentClinic.contactInfo?.addressLine2 || "",
        city: currentClinic.contactInfo?.city || "",
        zip: currentClinic.contactInfo?.zip || "",
        email: currentClinic.contactInfo?.email || "",
        notes: currentClinic.notes || "",
        methodForDrReport: currentClinic.methodForDrReport || ReportMethod.Email,
        methodForRxOrder: currentClinic.methodForRxOrder || ReportMethod.Email,
      });
      setIsClinicValid(true);
      // Fetch full clinic data to get associated doctors
      fetchClinicData(currentClinic.id);
    } else if (clinicId) {
      setExistingClinicEntityId("");
      setClinicForm({ ...initialClinicForm, id: clinicId });
      setIsClinicValid(false);
      setExistingDoctors([]);
      // Try to look up the clinic by BSNR
      fetchClinicData(clinicId);
    } else {
      setExistingClinicEntityId("");
      setClinicForm({ ...initialClinicForm });
      setIsClinicValid(false);
      setExistingDoctors([]);
    }

    // Set up new doctor form - auto-activate when doctorId (LANR) is provided
    if (doctorId) {
      // Only store as entity ID if it's NOT a 9-digit LANR (i.e. it's a real UUID)
      setExistingDoctorEntityId(doctorId.length === 9 ? "" : doctorId);
      const lanrValue = currentDoctor?.LANR || (doctorId.length === 9 ? doctorId : "");
      setNewDoctorForm({
        id: lanrValue,
        salutation: "",
        firstNames: "",
        lastName: initialDoctorName || "",
      });
      setIsDoctorValid(false);
    } else {
      setExistingDoctorEntityId("");
      setNewDoctorForm({
        id: "",
        salutation: "",
        firstNames: "",
        lastName: initialDoctorName || "",
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openAddEditDialog, currentClinic, clinicId, doctorId, currentDoctor, initialDoctorName]);

  // Also update when doctorId/currentDoctor changes independently
  useEffect(() => {
    if (doctorId) {
      setExistingDoctorEntityId(doctorId.length === 9 ? "" : doctorId);
      const lanrValue = currentDoctor?.LANR || (doctorId.length === 9 ? doctorId : "");
      setNewDoctorForm({
        id: lanrValue,
        salutation: "",
        firstNames: "",
        lastName: "",
      });
    }
  }, [doctorId, currentDoctor]);

  // ── Duplicate detection: search existing doctors by name ──
  // Skipped when an existing doctor was already auto-resolved by LANR.
  useEffect(() => {
    if (!openAddEditDialog) return;
    if (isDoctorValid) {
      setDoctorMatches([]);
      return;
    }
    const lastName = (newDoctorForm?.lastName || "").trim();
    const firstNames = (newDoctorForm?.firstNames || "").trim();
    const term = [lastName, firstNames].filter(Boolean).join(" ").trim();
    if (term.length < 2) {
      setDoctorMatches([]);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      setLoadingDoctorMatches(true);
      try {
        const result: any = await searchDoctor(1, 10, { searchTerm: term });
        if (cancelled) return;
        const list: any[] = result?.data ?? result ?? [];
        setDoctorMatches(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setDoctorMatches([]);
      } finally {
        if (!cancelled) setLoadingDoctorMatches(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [openAddEditDialog, isDoctorValid, newDoctorForm?.lastName, newDoctorForm?.firstNames, searchDoctor]);

  // ── Duplicate detection: search existing clinics by name ──
  useEffect(() => {
    if (!openAddEditDialog) return;
    if (isClinicValid) {
      setClinicNameMatches([]);
      return;
    }
    const term = (clinicForm.businessName || "").trim();
    if (term.length < 2) {
      setClinicNameMatches([]);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      setLoadingClinicMatches(true);
      try {
        const result: any = await searchClinic(1, 10, { searchTerm: term });
        if (cancelled) return;
        const list: any[] = result?.data ?? result ?? [];
        setClinicNameMatches(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setClinicNameMatches([]);
      } finally {
        if (!cancelled) setLoadingClinicMatches(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [openAddEditDialog, isClinicValid, clinicForm.businessName, searchClinic]);

  // ── Duplicate detection: search existing clinics by address (independent) ──
  useEffect(() => {
    if (!openAddEditDialog) return;
    if (isClinicValid) {
      setClinicAddressMatches([]);
      return;
    }
    const addrTerm = [clinicForm.addressLine1, clinicForm.zip, clinicForm.city]
      .filter(Boolean)
      .join(" ")
      .trim();
    if (addrTerm.length < 3) {
      setClinicAddressMatches([]);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const result: any = await searchClinic(1, 10, { searchTerm: addrTerm });
        if (cancelled) return;
        const list: any[] = result?.data ?? result ?? [];
        setClinicAddressMatches(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setClinicAddressMatches([]);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [openAddEditDialog, isClinicValid, clinicForm.addressLine1, clinicForm.zip, clinicForm.city, searchClinic]);

  // ── Handlers for "Diesen Arzt verwenden" / "Diese Praxis verwenden" ──
  const handleUseExistingDoctor = (existing: any) => {
    const newLANR = (newDoctorForm?.id || "").trim();
    if (existing.LANR) return; // Should not happen — button is disabled in this case
    setPendingAttach({ kind: "doctor", entity: existing, newLANR });
  };

  const handleUseExistingClinic = (existing: any) => {
    const newBSNR = (clinicForm.id || "").trim();
    if (existing.BSNR) return;
    setPendingAttach({ kind: "clinic", entity: existing, newBSNR });
  };

  const confirmAttach = async () => {
    if (!pendingAttach) return;
    setLoading(true);
    try {
      if (pendingAttach.kind === "doctor") {
        const doc = pendingAttach.entity;
        // Only call updateDoctorLANR if the doctor doesn't already have this LANR
        if (pendingAttach.newLANR && pendingAttach.newLANR.length === 9 && doc.LANR !== pendingAttach.newLANR) {
          await updateDoctorLANR(doc.id, pendingAttach.newLANR);
        }
        // Refresh and select that doctor in the form
        const refreshed: any = await getDoctorById(doc.id);
        const useDoctor = refreshed || doc;
        setIsDoctorValid(true);
        setExistingDoctorEntityId(useDoctor.id);
        setNewDoctorForm({
          id: useDoctor.LANR || pendingAttach.newLANR || "",
          salutation: useDoctor.contactInfo?.salutation || "",
          firstNames: useDoctor.contactInfo?.firstNames || "",
          lastName: useDoctor.contactInfo?.lastName || "",
        });
        setDoctorMatches([]);
      } else {
        const cli = pendingAttach.entity;
        // Only call updateClinicBSNR if the clinic doesn't already have this BSNR
        if (pendingAttach.newBSNR && pendingAttach.newBSNR.length === 9 && cli.BSNR !== pendingAttach.newBSNR) {
          await updateClinicBSNR(cli.id, pendingAttach.newBSNR);
        }
        const refreshed: any = await getClinicById(cli.id);
        const useClinic = refreshed || cli;
        setIsClinicValid(true);
        setExistingClinicEntityId(useClinic.id);
        setClinicForm({
          id: useClinic.BSNR || pendingAttach.newBSNR || "",
          businessName: useClinic.contactInfo?.businessName || "",
          phone1: useClinic.contactInfo?.phone1 || "",
          fax: useClinic.contactInfo?.fax || "",
          addressLine1: useClinic.contactInfo?.addressLine1 || "",
          addressLine2: useClinic.contactInfo?.addressLine2 || "",
          city: useClinic.contactInfo?.city || "",
          zip: useClinic.contactInfo?.zip || "",
          email: useClinic.contactInfo?.email || "",
          notes: useClinic.notes || "",
          methodForDrReport: useClinic.methodForDrReport || "",
          methodForRxOrder: useClinic.methodForRxOrder || "",
        });
        if (useClinic.doctors?.length) {
          setExistingDoctors(
            useClinic.doctors.map((d: any) => ({
              id: d.id,
              LANR: d.LANR || "",
              salutation: d.contactInfo?.salutation || "",
              firstNames: d.contactInfo?.firstNames || "",
              lastName: d.contactInfo?.lastName || "",
            }))
          );
        }
        setClinicNameMatches([]);
        setClinicAddressMatches([]);
      }
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || "Fehler beim Übernehmen.";
      setErrorMessage(msg);
      setOpenErrorDialog(true);
    } finally {
      setPendingAttach(null);
      setLoading(false);
    }
  };

  const fetchClinicData = async (id: string) => {
    if (!id || id.trim() === "") return;
    setLoading(true);
    try {
      const clinicData = (await getClinicById(id)) as ClinicForModal;
      if (clinicData) {
        setIsClinicValid(true);
        setExistingClinicEntityId(clinicData.id || id);
        setClinicForm({
          id: (clinicData as any).BSNR || "",
          businessName: clinicData.contactInfo?.businessName || "",
          phone1: clinicData.contactInfo?.phone1 || "",
          fax: clinicData.contactInfo?.fax || "",
          addressLine1: clinicData.contactInfo?.addressLine1 || "",
          addressLine2: clinicData.contactInfo?.addressLine2 || "",
          city: clinicData.contactInfo?.city || "",
          zip: clinicData.contactInfo?.zip || "",
          email: clinicData.contactInfo?.email || "",
          notes: clinicData.notes || "",
          methodForDrReport: clinicData.methodForDrReport || "",
          methodForRxOrder: clinicData.methodForRxOrder || "",
        });
        // Load existing doctors at this clinic
        if (clinicData.doctors && clinicData.doctors.length > 0) {
          const doctors = clinicData.doctors.map((doctor: any) => ({
            id: doctor.id,
            LANR: doctor.LANR || "",
            salutation: doctor.contactInfo?.salutation || "",
            firstNames: doctor.contactInfo?.firstNames || "",
            lastName: doctor.contactInfo?.lastName || "",
          }));
          setExistingDoctors(doctors);
        } else {
          setExistingDoctors([]);
        }
      } else {
        setIsClinicValid(false);
        setExistingClinicEntityId("");
        setExistingDoctors([]);
      }
    } catch {
      setIsClinicValid(false);
      setExistingClinicEntityId("");
      setExistingDoctors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // In edit mode, all changes are already persisted via EditDoctorDialog/EditClinicDialog/IdMigrationDialog.
    // Just propagate the latest entity to the parent and close.
    if (isEditMode) {
      try {
        const docId = existingDoctorEntityId || currentDoctor?.id;
        const cliId = existingClinicEntityId || currentClinic?.id;
        const [freshDoc, freshCli] = await Promise.all([
          docId ? getDoctorById(docId).catch(() => null) : Promise.resolve(null),
          cliId ? getClinicById(cliId).catch(() => null) : Promise.resolve(null),
        ]);
        if (freshDoc || freshCli) {
          setCreationResponse({
            ...(freshDoc || {}),
            doctorId: (freshDoc as any)?.id || docId || "",
            clinicId: (freshCli as any)?.id || cliId || "",
            contactInfo: (freshDoc as any)?.contactInfo,
            clinics: freshCli ? [freshCli] : (freshDoc as any)?.clinics,
            LANR: (freshDoc as any)?.LANR,
          });
        }
      } catch (e) {
        console.error("Error refreshing doctor/clinic after edit:", e);
      }
      handleCloseAddEditDialog();
      resetForm();
      return;
    }

    if (!newDoctorForm) return;

    const isFlexible = rxType && FLEXIBLE_ID_RX_TYPES.includes(rxType);
    const doctorVal = (newDoctorForm.id || "").trim();
    const clinicVal = (clinicForm.id || "").trim();

    if (isFlexible) {
      if (doctorVal.length !== 0 && doctorVal.length !== 9) {
        setErrorMessage("LANR muss entweder leer gelassen (wird automatisch generiert) oder genau 9 Zeichen lang sein.");
        setOpenErrorDialog(true);
        return;
      }
      if (clinicVal.length !== 0 && clinicVal.length !== 9) {
        setErrorMessage("BSNR muss entweder leer gelassen (wird automatisch generiert) oder genau 9 Zeichen lang sein.");
        setOpenErrorDialog(true);
        return;
      }
    } else {
      const effectiveDoctorLen = doctorVal.length || (isDoctorValid && existingDoctorEntityId ? 9 : 0);
      if (effectiveDoctorLen !== 9) {
        setErrorMessage("LANR muss genau 9 Zeichen lang sein.");
        setOpenErrorDialog(true);
        return;
      }
      if (clinicVal.length !== 0 && clinicVal.length !== 9) {
        setErrorMessage("BSNR muss entweder leer gelassen oder genau 9 Zeichen lang sein.");
        setOpenErrorDialog(true);
        return;
      }
    }

    const createDoctorClinicDto = {
      id: (isDoctorValid ? existingDoctorEntityId : null) || doctorVal || undefined,
      LANR: doctorVal.length === 9 ? doctorVal : undefined,
      contactInfo: {
        salutation: newDoctorForm.salutation || "",
        firstNames: newDoctorForm.firstNames || "",
        lastName: newDoctorForm.lastName || "",
      },
      clinics: [
        {
          id: (isClinicValid ? existingClinicEntityId : null) || clinicVal || undefined,
          BSNR: clinicVal.length === 9 ? clinicVal : undefined,
          contactInfo: {
            businessName: clinicForm.businessName,
            phone1: clinicForm.phone1,
            fax: clinicForm.fax,
            addressLine1: clinicForm.addressLine1,
            addressLine2: clinicForm.addressLine2,
            city: clinicForm.city,
            zip: clinicForm.zip,
            email: clinicForm.email,
          },
          notes: clinicForm.notes,
          methodForDrReport: clinicForm.methodForDrReport as ReportMethod || ReportMethod.Email,
          methodForRxOrder: clinicForm.methodForRxOrder as ReportMethod || ReportMethod.Email,
        },
      ],
    };

    setLoading(true);
    try {
      const response = await createDoctorWithClinic(createDoctorClinicDto);
      handleCloseAddEditDialog();
      resetForm();
      const res = response as any;
      const responseDoctorId = res?.id || existingDoctorEntityId || "";
      const responseClinicId = res?.clinics?.[0]?.id || existingClinicEntityId || "";
      setCreationResponse({
        ...res,
        doctorId: responseDoctorId,
        clinicId: responseClinicId,
        contactInfo: res?.contactInfo || createDoctorClinicDto.contactInfo,
        clinics: res?.clinics || createDoctorClinicDto.clinics,
        LANR: res?.LANR,
      });
      return response;
    } catch (error: any) {
      console.error("Error saving doctor and clinic:", error);
      const backendMsg = error?.response?.data?.message || error?.message || "";
      setErrorMessage(
        backendMsg
          ? `Fehler: ${backendMsg}`
          : "Beim Speichern von Arzt und Klinik ist ein Fehler aufgetreten. Bitte versuche es erneut."
      );
      setOpenErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setClinicForm(initialClinicForm);
    setNewDoctorForm(null);
    setExistingDoctors([]);
    setIsClinicValid(false);
    setIsDoctorValid(false);
    setExistingClinicEntityId("");
    setExistingDoctorEntityId("");
    setDoctorMatches([]);
    setClinicNameMatches([]);
    setClinicAddressMatches([]);
    setPendingAttach(null);
    setEditsHappened(false);
  };

  const handleCancel = async () => {
    // In edit mode, if edits happened (LANR/BSNR migration, contact info update) the changes
    // are already persisted server-side. Propagate the refreshed entity to the parent so the
    // UI reflects the new values instead of stale ones.
    if (isEditMode && editsHappened) {
      try {
        const docId = existingDoctorEntityId || currentDoctor?.id;
        const cliId = existingClinicEntityId || currentClinic?.id;
        const [freshDoc, freshCli] = await Promise.all([
          docId ? getDoctorById(docId).catch(() => null) : Promise.resolve(null),
          cliId ? getClinicById(cliId).catch(() => null) : Promise.resolve(null),
        ]);
        if (freshDoc || freshCli) {
          setCreationResponse({
            ...(freshDoc || {}),
            doctorId: (freshDoc as any)?.id || docId || "",
            clinicId: (freshCli as any)?.id || cliId || "",
            contactInfo: (freshDoc as any)?.contactInfo,
            clinics: freshCli ? [freshCli] : (freshDoc as any)?.clinics,
            LANR: (freshDoc as any)?.LANR,
          });
        }
      } catch (e) {
        console.error("Error refreshing doctor/clinic after edit:", e);
      }
    }
    resetForm();
    handleCloseAddEditDialog();
  };

  // Clinic input change (for create mode)
  const handleClinicInputChange = async (
    e: React.ChangeEvent<HTMLElement> | SelectChangeEvent<any>
  ) => {
    const { name, value } = e.target as HTMLInputElement;
    setClinicForm((prev) => ({ ...prev, [name]: value }));

    // Auto-lookup only once a full 9-digit BSNR is entered — partial input would
    // just produce a 404 per keystroke.
    if (name !== "id") return;
    const bsnr = value.trim();
    if (bsnr.length !== 9) {
      lastClinicLookupRef.current = null;
      setIsClinicValid(false);
      return;
    }
    if (lastClinicLookupRef.current === bsnr) return;
    lastClinicLookupRef.current = bsnr;
    fetchClinicData(bsnr);
  };

  // New doctor form input change
  const handleDoctorInputChange = async (
    e: React.ChangeEvent<HTMLElement> | SelectChangeEvent<any>
  ) => {
    const { name, value } = e.target as HTMLInputElement;
    setNewDoctorForm((prev) => ({
      ...((prev as any) || { id: "", salutation: "", firstNames: "", lastName: "" }),
      [name]: value,
    }));

    // Auto-complete doctor data only once a full 9-digit LANR is entered — partial
    // input would just produce a 404 per keystroke.
    if (name === "id") {
      const lanr = value.trim();
      if (lanr.length !== 9) {
        lastDoctorLookupRef.current = null;
        setIsDoctorValid(false);
        setExistingDoctorEntityId("");
        return;
      }
      if (lastDoctorLookupRef.current === lanr) return;
      lastDoctorLookupRef.current = lanr;
      try {
        const doctorData = await getDoctorById(lanr);
        if (doctorData) {
          setIsDoctorValid(true);
          setExistingDoctorEntityId(doctorData.id || "");
          setNewDoctorForm({
            id: (doctorData as any).LANR || value,
            salutation: doctorData.contactInfo?.salutation || "",
            firstNames: doctorData.contactInfo?.firstNames || "",
            lastName: doctorData.contactInfo?.lastName || "",
          });
        } else {
          setIsDoctorValid(false);
          setExistingDoctorEntityId("");
        }
      } catch {
        setIsDoctorValid(false);
        setExistingDoctorEntityId("");
      }
    }
  };

  // Edit clinic handler
  const handleEditClinic = () => {
    setClinicToEdit({
      ...clinicForm,
      id: existingClinicEntityId || "",
      entityId: existingClinicEntityId || "",
      BSNR: (currentClinic as any)?.BSNR || (clinicForm.id?.length === 9 ? clinicForm.id : ""),
    });
    setOpenEditClinicDialog(true);
  };

  const handleUpdateClinicFunc = async () => {
    setOpenEditClinicDialog(false);
    if (!clinicToEdit) return;
    setLoading(true);
    try {
      const payload = {
        id: existingClinicEntityId || clinicToEdit.id,
        contactInfo: {
          businessName: clinicToEdit.businessName || "",
          phone1: clinicToEdit.phone1 || "",
          fax: clinicToEdit.fax || "",
          addressLine1: clinicToEdit.addressLine1 || "",
          addressLine2: clinicToEdit.addressLine2 || "",
          city: clinicToEdit.city || "",
          zip: clinicToEdit.zip || "",
          email: clinicToEdit.email || "",
        },
        notes: clinicToEdit.notes || "",
        methodForDrReport: clinicToEdit.methodForDrReport || null,
        methodForRxOrder: clinicToEdit.methodForRxOrder || null,
      };
      await updateClinic(existingClinicEntityId || clinicToEdit.id, payload);
      setEditsHappened(true);
      setClinicForm({
        id: clinicToEdit.id,
        businessName: payload.contactInfo.businessName,
        phone1: payload.contactInfo.phone1,
        fax: payload.contactInfo.fax,
        addressLine1: payload.contactInfo.addressLine1,
        addressLine2: payload.contactInfo.addressLine2,
        city: payload.contactInfo.city,
        zip: payload.contactInfo.zip,
        email: payload.contactInfo.email,
        notes: payload.notes,
        methodForDrReport: payload.methodForDrReport as string || "",
        methodForRxOrder: payload.methodForRxOrder as string || "",
      });
    } catch (error) {
      console.error("Error updating clinic:", error);
      setErrorMessage("Fehler beim Aktualisieren der Praxis.");
      setOpenErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  // Edit doctor handler
  const handleEditDoctor = (doctor: any) => {
    setDoctorToEdit({ ...doctor });
    setOpenEditDoctorDialog(true);
  };

  const handleUpdateDoctorFunc = async () => {
    setOpenEditDoctorDialog(false);
    if (!doctorToEdit) return;
    try {
      const payload = {
        id: doctorToEdit.id,
        contactInfo: {
          salutation: doctorToEdit.salutation || "",
          firstNames: doctorToEdit.firstNames || "",
          lastName: doctorToEdit.lastName || "",
        },
      };
      await updateDoctorMutation.mutateAsync({ id: doctorToEdit.id, dto: payload });
      setEditsHappened(true);
      setExistingDoctors((prev) =>
        prev.map((doc) => (doc.id === doctorToEdit.id ? doctorToEdit : doc))
      );
    } catch (error) {
      console.error("Error updating doctor:", error);
      setErrorMessage("Fehler beim Aktualisieren des Arztes.");
      setOpenErrorDialog(true);
    }
  };

  const handleEditClinicInputChange = (e: React.ChangeEvent<HTMLElement> | any) => {
    const { name, value } = e.target as HTMLInputElement;
    setClinicToEdit((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleEditDoctorInputChange = (e: React.ChangeEvent<HTMLElement>) => {
    const { name, value } = e.target as HTMLInputElement;
    setDoctorToEdit((prev: any) => ({ ...prev, [name]: value }));
  };

  // Merge clinic matches from name + address (dedupe by id)
  const combinedClinicMatches = (() => {
    const seen = new Set<string>();
    const out: any[] = [];
    for (const c of [...clinicNameMatches, ...clinicAddressMatches]) {
      if (!c?.id || seen.has(c.id)) continue;
      seen.add(c.id);
      out.push(c);
    }
    return out;
  })();

  return (
    <Dialog open={openAddEditDialog} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>Arzt und Praxis verwalten</DialogTitle>
      <DialogContent>
        {(loading || createDoctorWithClinicMutation.isPending || updateDoctorMutation.isPending) && (
          <LoadingSpinner />
        )}

        <Grid container spacing={2}>
          {/* LEFT COLUMN: Clinic Info */}
          <Grid item xs={6}>
            <h3 style={{ marginTop: 10, marginBottom: 10 }}>Praxis Info</h3>
            {isClinicValid ? (
              <>
                <Button
                  style={{
                    paddingLeft: 10,
                    marginTop: 10,
                    marginRight: 10,
                    height: 30,
                    textTransform: "none",
                  }}
                  size="small"
                  color="primary"
                  onClick={handleEditClinic}
                >
                  bearbeiten
                </Button>
                <ClinicInfoDisplay clinic={{ ...clinicForm, BSNR: (currentClinic as any)?.BSNR || clinicForm.id || "" }} />
              </>
            ) : (
              <ClinicInfoEditForm
                clinicForm={clinicForm}
                onInputChange={handleClinicInputChange}
                ReportMethod={ReportMethod}
              />
            )}

            {/* Clinic duplicate matches */}
            {!isClinicValid && (loadingClinicMatches || combinedClinicMatches.length > 0) && (
              <div style={{ marginTop: 12, padding: 8, background: "#fff8e1", borderRadius: 4 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {loadingClinicMatches ? "Suche nach möglichen Duplikaten…" : "Mögliche bestehende Praxen:"}
                </Typography>
                {combinedClinicMatches.map((c) => {
                  const hasBSNR = !!c.BSNR;
                  const name = c.contactInfo?.businessName || (c.BSNR ? `BSNR: ${c.BSNR}` : "Unbenannte Praxis");
                  const addr = [c.contactInfo?.addressLine1, c.contactInfo?.zip, c.contactInfo?.city]
                    .filter(Boolean)
                    .join(", ");
                  return (
                    <div key={c.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0", borderTop: "1px solid #eee" }}>
                      <div>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          BSNR: {c.BSNR || "—"}
                          {addr ? ` • ${addr}` : ""}
                        </Typography>
                      </div>
                      <Button
                        size="small"
                        variant={hasBSNR ? "outlined" : "contained"}
                        disabled={hasBSNR}
                        onClick={() => handleUseExistingClinic(c)}
                        title={hasBSNR ? "Praxis hat bereits eine BSNR" : "Diese Praxis verwenden"}
                        sx={{ textTransform: "none", whiteSpace: "nowrap", minWidth: "auto", fontSize: "0.75rem" }}
                      >
                        {hasBSNR ? "Abweichende BSNR" : "Diese Praxis verwenden"}
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </Grid>

          {/* RIGHT COLUMN: Doctors */}
          <Grid item xs={6}>
            {/* New Doctor Entry Form - hidden in edit mode */}
            {!isEditMode && (
            <div style={{ marginBottom: "20px" }}>
              <h3 style={{ marginTop: 5, marginBottom: 0, paddingTop: 5 }}>
                Neuen Arzt anlegen {isDoctorValid && "(Arzt existiert)"}
              </h3>
              <Grid container spacing={2}>
                <Grid item xs={12} style={{paddingTop: 10}}>
                  <TextField
                    label="LANR"
                    name="id"
                    value={newDoctorForm?.id || ""}
                    onChange={handleDoctorInputChange}
                    fullWidth
                    margin="normal"
                    style={{ marginBottom: 0 }}
                    InputProps={{ style: { height: "50px" } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Anrede"
                    name="salutation"
                    value={newDoctorForm?.salutation || ""}
                    onChange={handleDoctorInputChange}
                    fullWidth
                    margin="normal"
                    style={{ marginTop: 0, marginBottom: 0 }}
                    InputProps={{ style: { height: "50px" } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Vorname(n)"
                    name="firstNames"
                    value={newDoctorForm?.firstNames || ""}
                    onChange={handleDoctorInputChange}
                    fullWidth
                    margin="normal"
                    style={{ marginTop: 0, marginBottom: 0 }}
                    InputProps={{ style: { height: "50px" } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Nachname"
                    name="lastName"
                    value={newDoctorForm?.lastName || ""}
                    onChange={handleDoctorInputChange}
                    fullWidth
                    margin="normal"
                    style={{ marginTop: 0, marginBottom: 0 }}
                    InputProps={{ style: { height: "50px" } }}
                  />
                </Grid>
              </Grid>
            </div>
            )}

            {/* Selected doctor (edit mode only) */}
            {isEditMode && (
              <div>
                <h3 style={{ marginTop: 5, marginBottom: 0, paddingTop: 5 }}>Ausgewählter Arzt</h3>
                {(() => {
                  const selDoc =
                    existingDoctors.find((d) => d.id === existingDoctorEntityId) ||
                    (currentDoctor && (currentDoctor.id || currentDoctor.LANR || currentDoctor.contactInfo)
                      ? {
                          id: currentDoctor.id,
                          LANR: currentDoctor.LANR,
                          salutation: currentDoctor.contactInfo?.salutation || currentDoctor.salutation,
                          firstNames: currentDoctor.contactInfo?.firstNames || currentDoctor.firstNames,
                          lastName: currentDoctor.contactInfo?.lastName || currentDoctor.lastName,
                        }
                      : null);
                  if (!selDoc) {
                    return (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: "italic" }}>
                        Kein Arzt ausgewählt. Wählen Sie unten einen vorhandenen Arzt aus.
                      </Typography>
                    );
                  }
                  return (
                    <div style={{ marginBottom: "16px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <h4 style={{ marginTop: 10, marginBottom: 5 }}>Arzt</h4>
                        <IconButton
                          size="small"
                          style={{ marginTop: 10 }}
                          onClick={() => handleEditDoctor(selDoc)}
                          title="Bearbeiten"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </div>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <Typography variant="body2">
                            <strong>LANR:</strong> {selDoc.LANR || "–"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2">
                            <strong>Anrede:</strong> {selDoc.salutation || "–"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2">
                            <strong>Vorname(n):</strong> {selDoc.firstNames || "–"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2">
                            <strong>Nachname:</strong> {selDoc.lastName || "–"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Doctor duplicate matches */}
            {!isEditMode && !isDoctorValid && (loadingDoctorMatches || doctorMatches.length > 0) && (
              <div style={{ marginTop: 12, padding: 8, background: "#fff8e1", borderRadius: 4 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {loadingDoctorMatches ? "Suche nach möglichen Duplikaten…" : "Mögliche bestehende Ärzte:"}
                </Typography>
                {doctorMatches.map((d) => {
                  const hasLANR = !!d.LANR;
                  const name = `${d.contactInfo?.lastName || ""}${d.contactInfo?.firstNames ? ", " + d.contactInfo.firstNames : ""}`.trim() || (d.LANR ? `LANR: ${d.LANR}` : "Unbekannter Arzt");
                  return (
                    <div key={d.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0", borderTop: "1px solid #eee" }}>
                      <div>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          LANR: {d.LANR || "—"}
                          {d.clinics?.length ? ` • ${d.clinics.length} Praxis(en)` : ""}
                        </Typography>
                      </div>
                      <Button
                        size="small"
                        variant={hasLANR ? "outlined" : "contained"}
                        disabled={hasLANR}
                        onClick={() => handleUseExistingDoctor(d)}
                        title={hasLANR ? "Arzt hat bereits eine LANR" : "Diesen Arzt verwenden"}
                        sx={{ textTransform: "none", whiteSpace: "nowrap", minWidth: "auto", fontSize: "0.75rem" }}
                      >
                        {hasLANR ? "Abweichende LANR" : "Diesen Arzt verwenden"}
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Existing Doctors at this clinic */}
            {isClinicValid && existingDoctors.length > 0 && (
              <div>
                <h3 style={{ marginTop: "20px", marginBottom: 0 }}>Vorhandene Ärzte</h3>
                {existingDoctors
                  .filter((doctor) => !isEditMode || doctor.id !== existingDoctorEntityId)
                  .map((doctor, index) => {
                  if (!doctor.id && !doctor.lastName) return null;
                  return (
                    <div key={doctor.id || index} style={{ marginBottom: "16px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <h4 style={{ marginTop: 10, marginBottom: 5 }}>Arzt {index + 1}</h4>
                        <IconButton
                          size="small"
                          style={{ marginTop: 10 }}
                          onClick={() => handleEditDoctor(doctor)}
                          title="Bearbeiten"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </div>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <Typography variant="body2">
                            <strong>LANR:</strong> {doctor.LANR || "–"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2">
                            <strong>Anrede:</strong> {doctor.salutation || "–"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2">
                            <strong>Vorname(n):</strong> {doctor.firstNames || "–"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2">
                            <strong>Nachname:</strong> {doctor.lastName || "–"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </div>
                  );
                })}
              </div>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={handleCancel}>
          Abbrechen
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={!isEditMode && (!newDoctorForm || (!newDoctorForm.id && !newDoctorForm.lastName))}
        >
          {isEditMode ? "Schließen" : "Speichern"}
        </Button>
      </DialogActions>

      {/* Sub-dialogs */}
      <ErrorDialog
        open={openErrorDialog}
        onClose={() => setOpenErrorDialog(false)}
        errorMessage={errorMessage}
      />
      <EditDoctorDialog
        open={openEditDoctorDialog}
        onClose={() => setOpenEditDoctorDialog(false)}
        doctorToEdit={doctorToEdit}
        onInputChange={handleEditDoctorInputChange}
        onSave={handleUpdateDoctorFunc}
        onLANRMigrated={(newLANR) => {
          setEditsHappened(true);
          setDoctorToEdit((prev: any) => (prev ? { ...prev, LANR: newLANR } : prev));
          setExistingDoctors((prev) =>
            prev.map((d) => (d.id === doctorToEdit?.id ? { ...d, LANR: newLANR } : d))
          );
          if (doctorToEdit?.id === existingDoctorEntityId) {
            setNewDoctorForm((prev) => (prev ? { ...prev, id: newLANR } : prev));
          }
        }}
      />
      <EditClinicDialog
        open={openEditClinicDialog}
        onClose={() => setOpenEditClinicDialog(false)}
        clinicToEdit={clinicToEdit}
        onInputChange={handleEditClinicInputChange}
        onSave={handleUpdateClinicFunc}
        ReportMethod={ReportMethod}
        onBSNRMigrated={(newBSNR) => {
          setEditsHappened(true);
          setClinicToEdit((prev: any) => (prev ? { ...prev, BSNR: newBSNR, id: newBSNR } : prev));
          setClinicForm((prev) => ({ ...prev, id: newBSNR }));
        }}
      />

      {/* Confirmation: attach LANR/BSNR to existing doctor/clinic */}
      <Dialog open={!!pendingAttach} onClose={() => setPendingAttach(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {pendingAttach?.kind === "doctor" ? "Bestehenden Arzt verwenden" : "Bestehende Praxis verwenden"}
        </DialogTitle>
        <DialogContent>
          {pendingAttach?.kind === "doctor" && (
            <Typography variant="body2">
              Es wird dem bestehenden Arzt
              {" "}
              <strong>
                {pendingAttach.entity?.contactInfo?.lastName || ""}
                {pendingAttach.entity?.contactInfo?.firstNames ? `, ${pendingAttach.entity.contactInfo.firstNames}` : ""}
              </strong>
              {pendingAttach.newLANR && pendingAttach.newLANR.length === 9 ? (
                <> die LANR <strong>{pendingAttach.newLANR}</strong> zugewiesen.</>
              ) : (
                <> kein neuer LANR-Wert zugewiesen (Feld leer).</>
              )}
              {" "}
              Anschließend wird dieses Rezept mit diesem Arzt verknüpft.
            </Typography>
          )}
          {pendingAttach?.kind === "clinic" && (
            <Typography variant="body2">
              Es wird der bestehenden Praxis
              {" "}
              <strong>{pendingAttach.entity?.contactInfo?.businessName || pendingAttach.entity?.id}</strong>
              {pendingAttach.newBSNR && pendingAttach.newBSNR.length === 9 ? (
                <> die BSNR <strong>{pendingAttach.newBSNR}</strong> zugewiesen.</>
              ) : (
                <> kein neuer BSNR-Wert zugewiesen (Feld leer).</>
              )}
              {" "}
              Anschließend wird dieses Rezept mit dieser Praxis verknüpft.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingAttach(null)}>Abbrechen</Button>
          <Button onClick={confirmAttach} variant="contained" color="primary">
            Bestätigen
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
});

export default VerordnungDialog;
